import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";
import { Button, CloseButton, ColorInput, NativeSelect, Tabs } from "@mantine/core";
import { normalizeNewsletterTextAlignment, normalizeNewsletterImageHeight } from "../../../packages/core/src/newsletterText";
import { RichTextInline } from "./RichTextInline";
import "./newsletterImageResize.css";

import {
  NewsletterIcon as Icon,
} from "./newsletterIcons";
import {
  ASSISTANT_NEWSLETTER_STYLE_OPTIONS,
  clampAssistantSectionMediaRatio,
  resolveAssistantBulletinPalette,
  resolveAssistantThemeColors,
  resolveAssistantThemeTokens,
  type AssistantDraftState,
  type AssistantNewsletterStyleOption,
  type AssistantSection,
  type AssistantThemeSettings,
} from "./newsletterUtils";

type AssistantVisualEditorProps = {
  draft: AssistantDraftState;
  previewHtml: string;
  isPreviewLoading: boolean;
  selectedSectionId: string | null;
  onSelectSection: (sectionId: string) => void;
  onUpdateDraft: (patch: Partial<AssistantDraftState>) => void;
  onUpdateTheme: (patch: Partial<AssistantThemeSettings>) => void;
  onUpdateSection: (sectionId: string, patch: Partial<AssistantSection>) => void;
  onOpenMediaLibrary: (sectionId: string) => void;
  onOpenLogoMediaLibrary: () => void;
  onOpenHeroMediaLibrary: () => void;
  onMoveSection: (sectionId: string, direction: "up" | "down") => void;
  onAddSection: (insertionIndex: number) => void;
  onDuplicateSection: (sectionId: string) => void;
  onRemoveSection: (sectionId: string) => void;
};

function renderNewsletterStylePreview(option: AssistantNewsletterStyleOption) {
  return (
    <span
      className={`newsletter-assistant-style-preview is-${option.value}`}
      aria-hidden="true"
      style={
        {
          "--style-preview-primary": option.preview[0],
          "--style-preview-accent": option.preview[1],
          "--style-preview-surface": option.preview[2],
        } as CSSProperties
      }
    >
      <span className="newsletter-assistant-style-preview-hero" />
      <span className="newsletter-assistant-style-preview-body">
        <span className="newsletter-assistant-style-preview-line line-title" />
        <span className="newsletter-assistant-style-preview-line" />
        <span className="newsletter-assistant-style-preview-line line-short" />
        <span className="newsletter-assistant-style-preview-cta" />
      </span>
    </span>
  );
}

export function AssistantVisualEditor({
  draft,
  previewHtml,
  isPreviewLoading,
  selectedSectionId,
  onSelectSection,
  onUpdateDraft,
  onUpdateTheme,
  onUpdateSection,
  onOpenMediaLibrary,
  onOpenLogoMediaLibrary,
  onOpenHeroMediaLibrary,
  onMoveSection,
  onAddSection,
  onDuplicateSection,
  onRemoveSection,
}: AssistantVisualEditorProps) {
  const alignmentOptions = [
    { value: "left", label: "À gauche" },
    { value: "center", label: "Centré" },
    { value: "right", label: "À droite" },
    { value: "justify", label: "Justifié" },
  ];
  const [contextTab, setContextTab] = useState<"section" | "newsletter">("section");
  const [contextBlock, setContextBlock] = useState<"hero" | "section">("section");
  const [canvasMode, setCanvasMode] = useState<"edit" | "preview">("edit");
  const [isImageEditorOpen, setIsImageEditorOpen] = useState(false);
  const [isButtonEditorOpen, setIsButtonEditorOpen] = useState(false);
  const [isStylePickerOpen, setIsStylePickerOpen] = useState(false);
  const sectionGridRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const pendingInsertionIndexRef = useRef<number | null>(null);
  const heroGridRef = useRef<HTMLDivElement | null>(null);
  const stylePickerRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{
    target: "hero" | "section";
    sectionId?: string;
    layout: Exclude<AssistantSection["media_layout"], "stacked">;
    left: number;
    right: number;
    width: number;
    gap: number;
  } | null>(null);
  const dragListenersRef = useRef<{
    move: ((event: PointerEvent) => void) | null;
    stop: (() => void) | null;
  }>({
    move: null,
    stop: null,
  });
  const selectedSection = draft.sections.find((section) => section.id === selectedSectionId) || draft.sections[0] || null;
  const selectedSectionIndex = draft.sections.findIndex((section) => section.id === selectedSection?.id);
  const heroIsSelected = contextTab === "section" && contextBlock === "hero";
  const themeTokens = resolveAssistantThemeTokens(draft.theme);
  const resolvedThemeColors = resolveAssistantThemeColors(draft.theme);
  const isFestiveStyle = draft.theme.style === "festive";
  const isBulletinStyle = draft.theme.style === "bulletin";
  const bulletinPalette = resolveAssistantBulletinPalette(draft.theme);
  const selectedStyleOption =
    ASSISTANT_NEWSLETTER_STYLE_OPTIONS.find((option) => option.value === draft.theme.style) ||
    ASSISTANT_NEWSLETTER_STYLE_OPTIONS[0];
  const isPreviewMode = canvasMode === "preview";
  const hasPreview = Boolean(previewHtml.trim());

  useEffect(() => {
    const index = pendingInsertionIndexRef.current;
    if (index === null) return;
    pendingInsertionIndexRef.current = null;
    const section = draft.sections[index];
    if (!section) return;
    const title = sectionGridRefs.current[section.id]
      ?.closest("article")
      ?.querySelector<HTMLElement>(".newsletter-assistant-section-title");
    title?.focus();
  }, [draft.sections]);

  function renderSectionInsertion(insertionIndex: number) {
    const label = insertionIndex === 0
      ? "Ajouter une section avant la première section"
      : `Ajouter une section après la section ${insertionIndex}`;
    return (
      <button
        type="button"
        className="newsletter-assistant-insert-section"
        aria-label={label}
        onClick={() => {
          pendingInsertionIndexRef.current = insertionIndex;
          setContextTab("section");
          setContextBlock("section");
          setIsImageEditorOpen(false);
          setIsButtonEditorOpen(false);
          onAddSection(insertionIndex);
        }}
      >
        <span className="newsletter-assistant-insert-line" aria-hidden="true" />
        <span className="newsletter-assistant-insert-action">
          <span className="newsletter-assistant-insert-plus" aria-hidden="true">+</span>
          <span className="newsletter-assistant-insert-label">Ajouter une section</span>
        </span>
        <span className="newsletter-assistant-insert-line" aria-hidden="true" />
      </button>
    );
  }
  const themeStyle = {
    "--assistant-primary": themeTokens.primary,
    "--assistant-accent": themeTokens.accent,
    "--assistant-secondary": themeTokens.secondary,
    "--assistant-canvas": themeTokens.canvas,
    "--assistant-card": themeTokens.card,
    "--assistant-border": themeTokens.border,
    "--assistant-text": themeTokens.text,
    "--assistant-muted": themeTokens.muted,
    "--assistant-primary-contrast-text": themeTokens.primaryContrastText,
    "--assistant-secondary-contrast-text": themeTokens.secondaryContrastText,
    "--assistant-button-text": themeTokens.buttonText,
    "--assistant-hero-text": themeTokens.heroText,
    "--assistant-radius": themeTokens.radius,
    "--assistant-heading-font": themeTokens.headingFont,
    "--assistant-body-font": themeTokens.bodyFont,
    "--assistant-kicker-spacing": themeTokens.kickerSpacing,
    "--assistant-button-radius": themeTokens.buttonRadius,
    "--assistant-hero-background": themeTokens.heroBackground,
    "--assistant-hero-body-text": themeTokens.heroBodyText,
    "--assistant-hero-border": themeTokens.heroBorder,
    "--assistant-hero-shadow": themeTokens.heroShadow,
    "--assistant-button-background": themeTokens.buttonBackground,
    "--assistant-button-border": themeTokens.buttonBorder,
    "--assistant-button-shadow": themeTokens.buttonShadow,
    "--assistant-button-decoration": themeTokens.buttonTextDecoration,
    "--assistant-canvas-padding": themeTokens.canvasPadding,
    "--assistant-card-padding": themeTokens.cardPadding,
    "--assistant-section-gap": themeTokens.sectionGap,
    "--assistant-card-shadow": themeTokens.cardShadow,
    "--assistant-container-width": themeTokens.containerWidth,
    "--assistant-hero-title-size": themeTokens.heroTitleSize,
    "--assistant-section-title-size": themeTokens.sectionTitleSize,
    "--assistant-body-size": themeTokens.bodySize,
    "--assistant-body-line-height": themeTokens.bodyLineHeight,
    "--assistant-media-width": themeTokens.mediaWidth,
    "--assistant-image-min-height": themeTokens.imageMinHeight,
    "--assistant-image-stacked-height": themeTokens.imageStackedHeight,
    "--assistant-bulletin-hero-band": bulletinPalette.heroBanner,
    "--assistant-bulletin-hero-band-text": bulletinPalette.heroBannerText,
    "--assistant-bulletin-hero-accent": bulletinPalette.heroAccent,
    "--assistant-bulletin-paper": bulletinPalette.paper,
    "--assistant-bulletin-paper-strong": bulletinPalette.paperStrong,
    "--assistant-bulletin-ink": bulletinPalette.ink,
    "--assistant-bulletin-muted": bulletinPalette.muted,
    "--assistant-bulletin-border": bulletinPalette.border,
  } as CSSProperties;
  const paletteGroupLabel = isFestiveStyle ? "Palette festive" : isBulletinStyle ? "Palette generee" : "Couleurs dominantes";
  const primaryColorLabel = isFestiveStyle ? "Ton principal" : isBulletinStyle ? "Couleur dominante" : "Couleur principale";
  const accentColorLabel = isFestiveStyle ? "Ton secondaire" : isBulletinStyle ? "Couleur secondaire" : "Couleur d'accent";
  const primarySwatches = isFestiveStyle
    ? ["#8F3538", "#A24739", "#7B4A2E", "#6B3A66", "#5D4C3B", "#2F5D73"]
    : isBulletinStyle
      ? ["#C95944", "#B64837", "#BF6B2D", "#8E3F56", "#4E7290", "#567D4E"]
    : ["#3152CA", "#22378F", "#C9652F", "#A2385E", "#28313F", "#3E6F5C"];
  const accentSwatches = isFestiveStyle
    ? ["#587286", "#42667A", "#2F5D73", "#677E5A", "#6A6284", "#8B5C72"]
    : isBulletinStyle
      ? ["#6F88B3", "#50828D", "#7BA46A", "#D3A24B", "#A16C58", "#8174A7"]
    : ["#7AA5FF", "#E3B778", "#FFB38A", "#F0A9C1", "#C7D2E5", "#9BD4B5"];

  function stopSectionResize() {
    if (dragListenersRef.current.move) {
      window.removeEventListener("pointermove", dragListenersRef.current.move);
    }
    if (dragListenersRef.current.stop) {
      window.removeEventListener("pointerup", dragListenersRef.current.stop);
      window.removeEventListener("pointercancel", dragListenersRef.current.stop);
    }
    dragListenersRef.current = { move: null, stop: null };
    dragStateRef.current = null;
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
  }

  useEffect(() => () => stopSectionResize(), []);

  useEffect(() => {
    if (!isStylePickerOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (stylePickerRef.current?.contains(event.target as Node)) {
        return;
      }
      setIsStylePickerOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsStylePickerOpen(false);
      }
    };

    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isStylePickerOpen]);

  function selectSection(sectionId: string) {
    setContextBlock("section");
    onSelectSection(sectionId);
  }

  function openHeroContextPanel() {
    setContextBlock("hero");
    setContextTab("section");
    setIsImageEditorOpen(true);
  }

  function buildMediaGridStyle(layout: AssistantSection["media_layout"], mediaWidthRatio: number | null) {
    if (layout === "stacked") {
      return undefined;
    }

    const mediaWidth =
      typeof mediaWidthRatio === "number"
        ? `${Math.round(mediaWidthRatio * 1000) / 10}%`
        : "var(--assistant-media-width, 180px)";

    return {
      "--assistant-section-media-width": mediaWidth,
      gridTemplateColumns:
        layout === "right"
          ? `minmax(0, 1fr) minmax(132px, ${mediaWidth})`
          : `minmax(132px, ${mediaWidth}) minmax(0, 1fr)`,
    } as CSSProperties;
  }

  function buildSectionGridStyle(section: AssistantSection) {
    return buildMediaGridStyle(section.media_layout, section.media_width_ratio);
  }

  function handleMediaResizeStart(
    event: ReactPointerEvent<HTMLDivElement>,
    target: "hero" | "section",
    layout: AssistantSection["media_layout"],
    gridElement: HTMLDivElement | null,
    sectionId?: string
  ) {
    if (layout === "stacked" || !gridElement) {
      return;
    }

    const rect = gridElement.getBoundingClientRect();
    const gap = Number.parseFloat(window.getComputedStyle(gridElement).columnGap || "0") || 0;

    stopSectionResize();
    if (target === "hero") {
      setContextBlock("hero");
      setContextTab("section");
    } else if (sectionId) {
      selectSection(sectionId);
    }
    event.preventDefault();
    event.stopPropagation();

    dragStateRef.current = {
      target,
      sectionId,
      layout,
      left: rect.left,
      right: rect.right,
      width: rect.width,
      gap,
    };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const activeDrag = dragStateRef.current;
      if (!activeDrag) {
        return;
      }

      const availableWidth = Math.max(activeDrag.width - activeDrag.gap, 220);
      const rawImageWidth =
        activeDrag.layout === "right"
          ? activeDrag.right - moveEvent.clientX - activeDrag.gap / 2
          : moveEvent.clientX - activeDrag.left - activeDrag.gap / 2;
      const nextRatio = clampAssistantSectionMediaRatio(rawImageWidth / availableWidth);

      if (nextRatio === null) {
        return;
      }

      if (activeDrag.target === "hero") {
        onUpdateDraft({ intro_image_width_ratio: nextRatio });
      } else if (activeDrag.sectionId) {
        onUpdateSection(activeDrag.sectionId, {
          media_width_ratio: nextRatio,
        });
      }
    };

    const handlePointerStop = () => {
      stopSectionResize();
    };

    dragListenersRef.current = {
      move: handlePointerMove,
      stop: handlePointerStop,
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerStop);
    window.addEventListener("pointercancel", handlePointerStop);
    document.body.style.setProperty("cursor", "col-resize");
    document.body.style.setProperty("user-select", "none");
  }

  function handleSectionResizeStart(event: ReactPointerEvent<HTMLDivElement>, section: AssistantSection) {
    handleMediaResizeStart(event, "section", section.media_layout, sectionGridRefs.current[section.id], section.id);
  }

  function handleHeroResizeStart(event: ReactPointerEvent<HTMLDivElement>) {
    handleMediaResizeStart(event, "hero", draft.intro_image_layout, heroGridRef.current);
  }

  function updateImageHeight(target: "hero" | "section", height: number, sectionId?: string) {
    const nextHeight = normalizeNewsletterImageHeight(height);
    if (target === "hero") {
      onUpdateDraft({ intro_image_height: nextHeight });
    } else if (sectionId) {
      onUpdateSection(sectionId, { media_height: nextHeight });
    }
  }

  function handleImageHeightResizeStart(
    event: ReactPointerEvent<HTMLDivElement>,
    target: "hero" | "section",
    height: number,
    sectionId?: string
  ) {
    if (event.button !== 0) return;
    stopSectionResize();
    event.preventDefault();
    event.stopPropagation();
    if (target === "hero") {
      setContextBlock("hero");
      setContextTab("section");
    } else if (sectionId) {
      selectSection(sectionId);
    }
    const initialY = event.clientY;
    const initialHeight = normalizeNewsletterImageHeight(height);
    const pointerId = event.pointerId;
    const move = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId === pointerId) {
        updateImageHeight(target, initialHeight + moveEvent.clientY - initialY, sectionId);
      }
    };
    const stop = () => stopSectionResize();
    dragListenersRef.current = { move, stop };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    document.body.style.setProperty("cursor", "row-resize");
    document.body.style.setProperty("user-select", "none");
  }

  function renderImageHeightHandle(target: "hero" | "section", value: number | null | undefined, sectionId?: string) {
    const height = normalizeNewsletterImageHeight(value);
    return (
      <div
        role="separator"
        tabIndex={0}
        aria-orientation="horizontal"
        aria-label={`Redimensionner la hauteur de l’illustration ${target === "hero" ? "du hero" : "de la section"}`}
        aria-valuemin={100}
        aria-valuemax={600}
        aria-valuenow={height}
        aria-valuetext={`${height} pixels`}
        className="newsletter-assistant-image-height-resizer"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => handleImageHeightResizeStart(event, target, height, sectionId)}
        onKeyDown={(event) => {
          const step = event.shiftKey ? 50 : 10;
          const next = event.key === "ArrowDown" ? height + step
            : event.key === "ArrowUp" ? height - step
            : event.key === "Home" ? 100
            : event.key === "End" ? 600 : null;
          if (next !== null) {
            event.preventDefault();
            event.stopPropagation();
            updateImageHeight(target, next, sectionId);
          }
        }}
      />
    );
  }

  function openSectionContextPanel(panel: "image" | "button", sectionId: string) {
    selectSection(sectionId);
    setContextTab("section");

    if (panel === "image") {
      setIsImageEditorOpen(true);
      return;
    }

    setIsButtonEditorOpen(true);
  }

  function toggleSectionContextPanel(panel: "image" | "button") {
    if (panel === "image") {
      setIsImageEditorOpen((current) => !current);
      return;
    }

    setIsButtonEditorOpen((current) => !current);
  }

  const heroLayoutClassName =
    draft.intro_image_layout === "stacked"
      ? "is-stacked"
      : draft.intro_image_layout === "right"
        ? "is-right"
        : "is-left";
  const heroImageButton = (
    <button
      key="hero-image"
      type="button"
      className={`newsletter-assistant-image-slot newsletter-assistant-hero-image-slot${
        draft.intro_image_url.trim() ? "" : " is-placeholder"
      }`}
      aria-label="Configurer l'image du hero"
      onClick={(event) => {
        event.stopPropagation();
        openHeroContextPanel();
      }}
    >
      {draft.intro_image_url.trim() ? (
        <img
          src={draft.intro_image_url}
          alt={draft.intro_image_alt.trim() || draft.intro_title.trim() || "Illustration du hero"}
        />
      ) : (
        <span>Ajouter une image dans le hero</span>
      )}
    </button>
  );
  const heroImageSlot = draft.intro_image_layout === "stacked" && draft.intro_image_url.trim() ? (
    <div
      key="hero-image"
      className="newsletter-assistant-stacked-media"
      style={{ "--assistant-stacked-image-height": `${normalizeNewsletterImageHeight(draft.intro_image_height)}px` } as CSSProperties}
    >
      {heroImageButton}
      {renderImageHeightHandle("hero", draft.intro_image_height)}
    </div>
  ) : heroImageButton;
  const heroCopySlot = (
    <div key="hero-copy" className="newsletter-assistant-section-copy newsletter-assistant-hero-copy">
      <InlineEditable
        value={draft.intro_kicker}
        placeholder="NEWSLETTER"
        className="newsletter-assistant-kicker"
        onChange={(value) => onUpdateDraft({ intro_kicker: value })}
      />
      <InlineEditable
        value={draft.intro_title}
        placeholder="Bonjour,"
        className="newsletter-assistant-hero-title"
        onChange={(value) => onUpdateDraft({ intro_title: value })}
      />
      {isBulletinStyle ? <div className="newsletter-assistant-bulletin-hero-accent" aria-hidden="true" /> : null}
      <RichTextInline
        style={{ textAlign: draft.text_align || "left" }}
        value={draft.intro_body}
        html={draft.intro_body_html}
        placeholder="Ajoutez ici une courte introduction pour donner le ton de la newsletter."
        className="newsletter-assistant-hero-body"
        onChange={(value, html) => onUpdateDraft({ intro_body: value, intro_body_html: html })}
      />
    </div>
  );

  return (
    <div className={`newsletter-assistant-visual-shell is-style-${draft.theme.style}`} style={themeStyle}>
      <div className={`newsletter-assistant-visual-layout${isPreviewMode ? " is-preview-mode" : ""}`}>
        <section className={`newsletter-assistant-canvas${isPreviewMode ? " is-preview-mode" : ""}`}>
          <div className="newsletter-assistant-canvas-topbar">
            <div className="newsletter-assistant-canvas-head">{isPreviewMode ? "APERCU EMAIL" : "NEWSLETTER CANVAS"}</div>
            <button
              type="button"
              className={`newsletter-assistant-preview-toggle${isPreviewMode ? " is-active" : ""}`}
              aria-label={isPreviewMode ? "Revenir en edition" : "Basculer en aperçu"}
              title={isPreviewMode ? "Revenir en edition" : "Basculer en aperçu"}
              aria-pressed={isPreviewMode}
              onClick={() => setCanvasMode((current) => (current === "edit" ? "preview" : "edit"))}
            >
              <Icon iconId={isPreviewMode ? "icon-pen" : "icon-eye"} size={16} />
            </button>
          </div>

          {isPreviewMode ? (
            <div className="newsletter-assistant-preview-shell">
              <div className="newsletter-assistant-preview-note">
                <strong>{isPreviewLoading ? "Mise à jour en cours" : "Rendu email"}</strong>
                <span>
                  {isPreviewLoading
                    ? "L'aperçu se rafraîchit automatiquement pendant vos modifications."
                    : "Voici le rendu email tel qu'il sera généré par l'assistant."}
                </span>
              </div>

              {hasPreview ? (
                <div
                  className="newsletter-email-preview newsletter-email-preview-assistant"
                  onClick={(event) => {
                    const target = event.target as HTMLElement;
                    if (target.closest("a")) {
                      event.preventDefault();
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              ) : (
                <div className="newsletter-assistant-preview-empty">
                  <strong>Aperçu indisponible pour le moment</strong>
                  <p>Ajoutez ou modifiez le contenu de la newsletter et le rendu apparaîtra ici.</p>
                </div>
              )}
            </div>
          ) : (
            <>

          <button
            type="button"
            className={`newsletter-assistant-header-logo-slot${draft.header_logo_url.trim() ? " has-logo" : " is-placeholder"}`}
            aria-label="Configurer le logo de la newsletter"
            onClick={() => setContextTab("newsletter")}
          >
            {draft.header_logo_url.trim() ? (
              <img
                src={draft.header_logo_url}
                alt={draft.header_logo_alt.trim() || "Logo newsletter"}
                className="newsletter-assistant-header-logo-image"
              />
            ) : (
              <span>Ajouter un logo au-dessus du hero</span>
            )}
          </button>

          <article
            className={`newsletter-assistant-canvas-card newsletter-assistant-canvas-card-hero newsletter-assistant-section-canvas ${heroLayoutClassName}${heroIsSelected ? " is-selected" : ""}`}
            onClick={openHeroContextPanel}
          >
            <div className="newsletter-assistant-canvas-card-head">
              <span>Hero / Introduction</span>
            </div>

            <div
              ref={heroGridRef}
              className="newsletter-assistant-section-grid newsletter-assistant-hero-grid"
              style={buildMediaGridStyle(draft.intro_image_layout, draft.intro_image_width_ratio)}
            >
              {draft.intro_image_layout === "right" ? [heroCopySlot, heroImageSlot] : [heroImageSlot, heroCopySlot]}

              {draft.intro_image_layout !== "stacked" && heroIsSelected ? (
                <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Redimensionner l'image et le texte du hero"
                  className={`newsletter-assistant-section-resizer is-${draft.intro_image_layout} is-selected`}
                  onClick={(event) => event.stopPropagation()}
                  onPointerDown={handleHeroResizeStart}
                />
              ) : null}
            </div>
          </article>

          {renderSectionInsertion(0)}

          {draft.sections.map((section, index) => {
            const isSelected = selectedSection?.id === section.id;
            const layoutClassName =
              section.media_layout === "stacked"
                ? "is-stacked"
                : section.media_layout === "right"
                  ? "is-right"
                  : "is-left";
            const festiveToneClassName = draft.theme.style === "festive" ? (index % 2 === 0 ? " is-tone-warm" : " is-tone-cool") : "";
            const bulletinTone =
              isBulletinStyle ? bulletinPalette.sectionTones[index % bulletinPalette.sectionTones.length] : null;
            const bulletinToneStyle =
              bulletinTone
                ? ({
                    "--assistant-bulletin-band": bulletinTone.background,
                    "--assistant-bulletin-band-text": bulletinTone.text,
                    "--assistant-bulletin-surface": bulletinTone.surface,
                    "--assistant-bulletin-surface-text": bulletinTone.surfaceText,
                  } as CSSProperties)
                : undefined;
            const imageButton = (
              <button
                key={`${section.id}-image`}
                type="button"
                className={`newsletter-assistant-image-slot${section.image_url.trim() ? "" : " is-placeholder"}`}
                aria-label={`Ouvrir les réglages de l'image de la section ${index + 1}`}
                onClick={(event) => {
                  event.stopPropagation();
                  openSectionContextPanel("image", section.id);
                }}
              >
                {section.image_url.trim() ? (
                  <img
                    src={section.image_url}
                    alt={section.image_alt.trim() || section.title.trim() || "Illustration"}
                  />
                ) : (
                  <span>IMAGE</span>
                )}
              </button>
            );
            const imageSlot = section.media_layout === "stacked" && section.image_url.trim() ? (
              <div
                key={`${section.id}-image`}
                className="newsletter-assistant-stacked-media"
                style={{ "--assistant-stacked-image-height": `${normalizeNewsletterImageHeight(section.media_height)}px` } as CSSProperties}
              >
                {imageButton}
                {renderImageHeightHandle("section", section.media_height, section.id)}
              </div>
            ) : imageButton;
            const titleEditable = (
              <InlineEditable
                value={section.title}
                placeholder={index === 0 ? "Nouvel atelier" : "Titre de section"}
                className="newsletter-assistant-section-title"
                onChange={(value) => onUpdateSection(section.id, { title: value })}
              />
            );
            const copySlot = (
              <div key={`${section.id}-copy`} className="newsletter-assistant-section-copy">
                {isBulletinStyle ? null : titleEditable}
                <RichTextInline
                  style={{ textAlign: section.text_align || draft.text_align || "left" }}
                  value={section.body}
                  html={section.body_html}
                  placeholder="Une courte description editable, sur plusieurs lignes, avec un ton simple et lisible."
                  className="newsletter-assistant-section-body"
                  onChange={(value, html) => onUpdateSection(section.id, { body: value, body_html: html })}
                />

                <button
                  type="button"
                  className={`newsletter-assistant-button-chip${
                    section.button_label.trim() && section.button_url.trim() ? "" : " is-placeholder"
                  }`}
                  aria-label={`Ouvrir les réglages du bouton de la section ${index + 1}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    openSectionContextPanel("button", section.id);
                  }}
                >
                  {section.button_label.trim() || "Bouton d'action"}
                </button>
              </div>
            );

            return (
              <div key={section.id} className="newsletter-assistant-section-stack">
                <article
                  className={`newsletter-assistant-canvas-card newsletter-assistant-section-canvas ${layoutClassName}${festiveToneClassName}${isSelected ? " is-selected" : ""}`}
                  style={bulletinToneStyle}
                  onClick={() => selectSection(section.id)}
                >
                  {isSelected ? (
                    <div className="newsletter-assistant-section-toolbar" role="toolbar" aria-label={`Actions de la section ${index + 1}`}>
                      <button
                        type="button"
                        className="newsletter-assistant-section-toolbar-button"
                        aria-label={`Monter la section ${index + 1}`}
                        title={`Monter la section ${index + 1}`}
                        disabled={index === 0}
                        onClick={(event) => {
                          event.stopPropagation();
                          onMoveSection(section.id, "up");
                        }}
                      >
                        <Icon iconId="icon-arrow-up" size={16} />
                      </button>
                      <button
                        type="button"
                        className="newsletter-assistant-section-toolbar-button"
                        aria-label={`Descendre la section ${index + 1}`}
                        title={`Descendre la section ${index + 1}`}
                        disabled={index === draft.sections.length - 1}
                        onClick={(event) => {
                          event.stopPropagation();
                          onMoveSection(section.id, "down");
                        }}
                      >
                        <Icon iconId="icon-arrow-down" size={16} />
                      </button>
                      <span className="newsletter-assistant-section-toolbar-separator" aria-hidden="true" />
                      <button
                        type="button"
                        className="newsletter-assistant-section-toolbar-button"
                        aria-label={`Dupliquer la section ${index + 1}`}
                        title={`Dupliquer la section ${index + 1}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          onDuplicateSection(section.id);
                        }}
                      >
                        <Icon iconId="icon-copy" size={16} />
                      </button>
                      <button
                        type="button"
                        className="newsletter-assistant-section-toolbar-button is-danger"
                        aria-label={`Supprimer la section ${index + 1}`}
                        title={`Supprimer la section ${index + 1}`}
                        disabled={draft.sections.length === 1}
                        onClick={(event) => {
                          event.stopPropagation();
                          onRemoveSection(section.id);
                        }}
                      >
                        <Icon iconId="icon-trash" size={16} />
                      </button>
                    </div>
                  ) : null}

                  <div className="newsletter-assistant-canvas-card-head">
                    <span>Section {index + 1}</span>
                  </div>

                  {isBulletinStyle ? <div className="newsletter-assistant-section-band">{titleEditable}</div> : null}

                  <div
                    ref={(node) => {
                      sectionGridRefs.current[section.id] = node;
                    }}
                    className="newsletter-assistant-section-grid"
                    style={buildSectionGridStyle(section)}
                  >
                    {section.media_layout === "right" ? [copySlot, imageSlot] : [imageSlot, copySlot]}

                    {section.media_layout !== "stacked" && isSelected ? (
                      <div
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={`Redimensionner l'image et le texte de la section ${index + 1}`}
                        className={`newsletter-assistant-section-resizer is-${section.media_layout} is-selected`}
                        onClick={(event) => event.stopPropagation()}
                        onPointerDown={(event) => handleSectionResizeStart(event, section)}
                      />
                    ) : null}
                  </div>
                </article>
                {renderSectionInsertion(index + 1)}
              </div>
            );
          })}

          <article className="newsletter-assistant-canvas-card newsletter-assistant-canvas-card-footer">
            <div className="newsletter-assistant-canvas-card-head">
              <span>Footer</span>
            </div>
            <RichTextInline
              style={{ textAlign: draft.text_align || "left" }}
              value={draft.closing_note}
              html={draft.closing_note_html}
              placeholder="Merci pour votre lecture."
              className="newsletter-assistant-footer-note"
              onChange={(value, html) => onUpdateDraft({ closing_note: value, closing_note_html: html })}
            />
            <div className="newsletter-assistant-system-footer" contentEditable={false}>
              <a href="#" aria-disabled="true" onClick={(event) => event.preventDefault()}>Se désinscrire</a>
              <a href="https://relyoapp.fr" target="_blank" rel="noreferrer">
                <span>Fait avec Relyo</span>
                <img src="/api/newsletter/brand/logo.png" alt="Relyo" width={64} />
              </a>
            </div>
          </article>
            </>
          )}
        </section>

        {!isPreviewMode ? (
          <aside className="newsletter-assistant-context-panel">
          <div className="newsletter-assistant-context-card">
            <Tabs
              className="newsletter-assistant-context-tabs"
              value={contextTab}
              onChange={(value) => setContextTab((value as "section" | "newsletter") || "section")}
            >
              <Tabs.List grow>
                <Tabs.Tab value="section">Section</Tabs.Tab>
                <Tabs.Tab value="newsletter">Newsletter</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="section" pt="md">
                {contextBlock === "hero" ? (
                  <div className="newsletter-assistant-context-panel-body">
                    <div className="newsletter-assistant-context-head">
                      <h4>Hero / Introduction</h4>
                    </div>

                    <div className={`newsletter-assistant-context-collapsible is-open`}>
                      <button
                        type="button"
                        className="newsletter-assistant-context-collapsible-toggle"
                        aria-expanded="true"
                        aria-controls="assistant-hero-image-panel"
                        onClick={() => setIsImageEditorOpen(true)}
                      >
                        <span className="newsletter-assistant-context-collapsible-copy">
                          <strong>Image</strong>
                          <span>{draft.intro_image_url.trim() ? "Illustration configurée" : "Aucune image pour le moment"}</span>
                        </span>
                        <Icon iconId="icon-arrow-down" size={16} />
                      </button>

                      <div
                        id="assistant-hero-image-panel"
                        className="newsletter-assistant-context-collapsible-body"
                      >
                        <div className="newsletter-assistant-context-group">
                          <strong>Position d'image</strong>
                          <div className="newsletter-assistant-layout-segmented" role="radiogroup" aria-label="Disposition image du hero">
                            {[
                              { label: "A gauche", value: "left" },
                              { label: "Au-dessus", value: "stacked" },
                              { label: "A droite", value: "right" },
                            ].map((option) => {
                              const isActive = draft.intro_image_layout === option.value;
                              return (
                                <button
                                  key={option.value}
                                  type="button"
                                  role="radio"
                                  aria-checked={isActive}
                                  className={`newsletter-assistant-layout-option${isActive ? " is-active" : ""}`}
                                  onClick={() =>
                                    onUpdateDraft({
                                      intro_image_layout: option.value as AssistantSection["media_layout"],
                                    })
                                  }
                                >
                                  {option.label}
                                </button>
                              );
                            })}
                          </div>
                          <Button
                            type="button"
                            variant="light"
                            color="easyBlue"
                            radius="xl"
                            onClick={onOpenHeroMediaLibrary}
                          >
                            Ajouter une image
                          </Button>
                          {draft.intro_image_layout === "stacked" ? (
                            <label>
                              Hauteur de l’illustration
                              <input type="number" min={100} max={600} step={10}
                                value={normalizeNewsletterImageHeight(draft.intro_image_height)}
                                onChange={(event) => updateImageHeight("hero", event.target.valueAsNumber)} />
                            </label>
                          ) : null}
                          <label>
                            Texte alternatif
                            <input
                              type="text"
                              value={draft.intro_image_alt}
                              onChange={(event) => onUpdateDraft({ intro_image_alt: event.target.value })}
                            />
                          </label>
                          {draft.intro_image_url.trim() ? (
                            <Button
                              type="button"
                              variant="subtle"
                              color="gray"
                              radius="xl"
                              onClick={() => onUpdateDraft({ intro_image_url: "", intro_image_alt: "" })}
                            >
                              Retirer l'image du hero
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="newsletter-assistant-context-tip">
                      <strong>Astuce</strong>
                      <p>Le hero utilise les mêmes positions d'image que les sections. Le style choisi définit la position par défaut.</p>
                    </div>
                  </div>
                ) : selectedSection ? (
                  <div className="newsletter-assistant-context-panel-body">
                    <div className="newsletter-assistant-context-head">
                      <h4>Section {selectedSectionIndex + 1}</h4>
                    </div>

                    <NativeSelect
                      label="Alignement du texte de la section"
                      description="Concerne les paragraphes de cette section."
                      data={[{ value: "", label: "Suivre la newsletter" }, ...alignmentOptions]}
                      value={selectedSection.text_align || ""}
                      onChange={(event) => onUpdateSection(selectedSection.id, { text_align: normalizeNewsletterTextAlignment(event.currentTarget.value) })}
                    />

                    <div className={`newsletter-assistant-context-collapsible${isImageEditorOpen ? " is-open" : ""}`}>
                      <button
                        type="button"
                        className="newsletter-assistant-context-collapsible-toggle"
                        aria-expanded={isImageEditorOpen}
                        aria-controls={`assistant-image-panel-${selectedSection.id}`}
                        onClick={() => toggleSectionContextPanel("image")}
                      >
                        <span className="newsletter-assistant-context-collapsible-copy">
                          <strong>Image</strong>
                          <span>{selectedSection.image_url.trim() ? "Illustration configurée" : "Aucune image pour le moment"}</span>
                        </span>
                        <Icon iconId="icon-arrow-down" size={16} />
                      </button>

                      <div
                        id={`assistant-image-panel-${selectedSection.id}`}
                        className="newsletter-assistant-context-collapsible-body"
                        hidden={!isImageEditorOpen}
                      >
                        <div className="newsletter-assistant-context-group">
                          <strong>Position d'image</strong>
                          <div className="newsletter-assistant-layout-segmented" role="radiogroup" aria-label="Disposition image">
                            {[
                              { label: "A gauche", value: "left" },
                              { label: "Au-dessus", value: "stacked" },
                              { label: "A droite", value: "right" },
                            ].map((option) => {
                              const isActive = selectedSection.media_layout === option.value;
                              return (
                                <button
                                  key={option.value}
                                  type="button"
                                  role="radio"
                                  aria-checked={isActive}
                                  className={`newsletter-assistant-layout-option${isActive ? " is-active" : ""}`}
                                  onClick={() =>
                                    onUpdateSection(selectedSection.id, {
                                      media_layout: option.value as AssistantSection["media_layout"],
                                    })
                                  }
                                >
                                  {option.label}
                                </button>
                              );
                            })}
                          </div>
                          <Button
                            type="button"
                            variant="light"
                            color="easyBlue"
                            radius="xl"
                            onClick={() => onOpenMediaLibrary(selectedSection.id)}
                          >
                            Ajouter une image
                          </Button>
                          {selectedSection.media_layout === "stacked" ? (
                            <label>
                              Hauteur de l’illustration
                              <input type="number" min={100} max={600} step={10}
                                value={normalizeNewsletterImageHeight(selectedSection.media_height)}
                                onChange={(event) => updateImageHeight("section", event.target.valueAsNumber, selectedSection.id)} />
                            </label>
                          ) : null}
                          <label>
                            Texte alternatif
                            <input
                              type="text"
                              value={selectedSection.image_alt}
                              onChange={(event) => onUpdateSection(selectedSection.id, { image_alt: event.target.value })}
                            />
                          </label>
                          {selectedSection.image_url.trim() ? (
                            <Button
                              type="button"
                              variant="subtle"
                              color="gray"
                              radius="xl"
                              onClick={() => onUpdateSection(selectedSection.id, { image_url: "", image_alt: "" })}
                            >
                              Retirer l'image
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className={`newsletter-assistant-context-collapsible${isButtonEditorOpen ? " is-open" : ""}`}>
                      <button
                        type="button"
                        className="newsletter-assistant-context-collapsible-toggle"
                        aria-expanded={isButtonEditorOpen}
                        aria-controls={`assistant-button-panel-${selectedSection.id}`}
                        onClick={() => toggleSectionContextPanel("button")}
                      >
                        <span className="newsletter-assistant-context-collapsible-copy">
                          <strong>Bouton d'action</strong>
                          <span>{selectedSection.button_label.trim() ? selectedSection.button_label.trim() : "Aucun bouton configuré"}</span>
                        </span>
                        <Icon iconId="icon-arrow-down" size={16} />
                      </button>

                      <div
                        id={`assistant-button-panel-${selectedSection.id}`}
                        className="newsletter-assistant-context-collapsible-body"
                        hidden={!isButtonEditorOpen}
                      >
                        <div className="newsletter-assistant-context-group">
                          <label>
                            Libellé du bouton
                            <input
                              type="text"
                              value={selectedSection.button_label}
                              onChange={(event) => onUpdateSection(selectedSection.id, { button_label: event.target.value })}
                            />
                          </label>
                          <label>
                            Lien du bouton
                            <input
                              type="url"
                              placeholder="https://..."
                              value={selectedSection.button_url}
                              onChange={(event) => onUpdateSection(selectedSection.id, { button_url: event.target.value })}
                            />
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="newsletter-assistant-context-tip">
                      <strong>Astuce</strong>
                      <p>Les champs vides ne s&apos;affichent pas. Les actions de section apparaissent directement sur la carte sélectionnée.</p>
                    </div>
                  </div>
                ) : (
                  <div className="newsletter-assistant-context-card newsletter-assistant-context-empty">
                    <strong>Sélectionnez une section</strong>
                    <p>Le panneau contextuel apparait ici quand une section est active.</p>
                  </div>
                )}
              </Tabs.Panel>

              <Tabs.Panel value="newsletter" pt="md">
                <div className="newsletter-assistant-context-panel-body">
                  <div className="newsletter-assistant-context-head">
                    <h4>Réglages newsletter</h4>
                  </div>

                  <NativeSelect
                    label="Alignement du texte de la newsletter"
                    description="Introduction, conclusion et sections qui suivent la newsletter."
                    data={alignmentOptions}
                    value={draft.text_align || "left"}
                    onChange={(event) => onUpdateDraft({ text_align: normalizeNewsletterTextAlignment(event.currentTarget.value) || "left" })}
                  />

                  <div className="newsletter-assistant-context-group">
                    <strong>Style</strong>
                    <div ref={stylePickerRef} className="newsletter-assistant-style-picker">
                      <button
                        type="button"
                        className={`newsletter-assistant-style-trigger${isStylePickerOpen ? " is-open" : ""}`}
                        aria-label="Style newsletter"
                        aria-haspopup="listbox"
                        aria-expanded={isStylePickerOpen}
                        onClick={() => setIsStylePickerOpen((current) => !current)}
                      >
                        {renderNewsletterStylePreview(selectedStyleOption)}
                        <span className="newsletter-assistant-style-copy">
                          <strong>{selectedStyleOption.label}</strong>
                          <span>{selectedStyleOption.promise}</span>
                        </span>
                        <Icon iconId="icon-arrow-down" size={16} />
                      </button>

                      {isStylePickerOpen ? (
                        <div className="newsletter-assistant-style-dropdown" role="listbox" aria-label="Choisir un style de newsletter">
                          <div className="newsletter-assistant-style-options">
                            {ASSISTANT_NEWSLETTER_STYLE_OPTIONS.map((option) => {
                              const isActive = option.value === selectedStyleOption.value;
                              return (
                                <button
                                  key={option.value}
                                  type="button"
                                  role="option"
                                  aria-selected={isActive}
                                  className={`newsletter-assistant-style-card${isActive ? " is-active" : ""}`}
                                  onClick={() => {
                                    onUpdateTheme({
                                      style: option.value as AssistantThemeSettings["style"],
                                    });
                                    setIsStylePickerOpen(false);
                                  }}
                                >
                                  {renderNewsletterStylePreview(option)}
                                  <span className="newsletter-assistant-style-copy">
                                    <strong>{option.label}</strong>
                                    <span>{option.promise}</span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="newsletter-assistant-context-group">
                    <strong>{paletteGroupLabel}</strong>
                    {isFestiveStyle ? (
                      <p className="newsletter-assistant-inline-hint">
                        Le fond papier et le jaune d&apos;accent restent fixes. Vous choisissez ici les deux tons forts du style.
                      </p>
                    ) : isBulletinStyle ? (
                      <p className="newsletter-assistant-inline-hint">
                        Choisissez 2 couleurs principales. Le style Bulletin genere ensuite ses couleurs d&apos;accompagnement pour les bandeaux.
                      </p>
                    ) : null}
                    <ColorInput
                      label={primaryColorLabel}
                      format="hex"
                      placeholder={resolvedThemeColors.primary_color}
                      withEyeDropper={false}
                      rightSection={
                        draft.theme.primary_color ? (
                          <CloseButton
                            aria-label={`Réinitialiser ${primaryColorLabel.toLowerCase()}`}
                            size="sm"
                            variant="transparent"
                            onClick={() => onUpdateTheme({ primary_color: "" })}
                          />
                        ) : null
                      }
                      swatches={primarySwatches}
                      value={draft.theme.primary_color || resolvedThemeColors.primary_color}
                      onChange={(value) => onUpdateTheme({ primary_color: value || "" })}
                    />
                    <ColorInput
                      label={accentColorLabel}
                      format="hex"
                      placeholder={resolvedThemeColors.accent_color}
                      withEyeDropper={false}
                      rightSection={
                        draft.theme.accent_color ? (
                          <CloseButton
                            aria-label={`Réinitialiser ${accentColorLabel.toLowerCase()}`}
                            size="sm"
                            variant="transparent"
                            onClick={() => onUpdateTheme({ accent_color: "" })}
                          />
                        ) : null
                      }
                      swatches={accentSwatches}
                      value={draft.theme.accent_color || resolvedThemeColors.accent_color}
                      onChange={(value) => onUpdateTheme({ accent_color: value || "" })}
                    />
                  </div>

                  <div className="newsletter-assistant-context-group">
                    <strong>Logo</strong>
                    <Button
                      type="button"
                      variant="light"
                      color="easyBlue"
                      radius="xl"
                      onClick={onOpenLogoMediaLibrary}
                    >
                      Choisir dans la médiathèque
                    </Button>
                    <label>
                      Texte alternatif du logo
                      <input
                        type="text"
                        value={draft.header_logo_alt}
                        onChange={(event) => onUpdateDraft({ header_logo_alt: event.target.value })}
                      />
                    </label>
                    {draft.header_logo_url.trim() ? (
                      <Button
                        type="button"
                        variant="subtle"
                        color="gray"
                        radius="xl"
                        onClick={() => onUpdateDraft({ header_logo_url: "", header_logo_alt: "" })}
                      >
                        Retirer le logo
                      </Button>
                    ) : null}
                  </div>

                  <div className="newsletter-assistant-context-tip">
                    <strong>Aperçu</strong>
                    <p>Ces réglages modifient le logo, les cartes, les boutons et les couleurs du rendu email.</p>
                  </div>
                </div>
              </Tabs.Panel>
            </Tabs>
          </div>
          </aside>
        ) : null}
      </div>
    </div>
  );
}

type InlineEditableProps = {
  value: string;
  placeholder: string;
  className: string;
  multiline?: boolean;
  onChange: (value: string) => void;
};

function InlineEditable({ value, placeholder, className, multiline = false, onChange }: InlineEditableProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    if (ref.current.innerText !== value) {
      ref.current.innerText = value;
    }
  }, [value]);

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      className={`newsletter-assistant-inline-editable ${className}`}
      data-placeholder={placeholder}
      onClick={(event) => event.stopPropagation()}
      onInput={(event) => onChange(normalizeEditableValue(event.currentTarget.innerText, multiline))}
      onKeyDown={(event) => {
        if (!multiline && event.key === "Enter") {
          event.preventDefault();
          (event.currentTarget as HTMLDivElement).blur();
        }
      }}
    />
  );
}

function normalizeEditableValue(value: string, multiline: boolean) {
  const normalized = String(value || "").replace(/\u00a0/g, " ").replace(/\r/g, "");
  if (multiline) {
    return normalized.replace(/\n{3,}/g, "\n\n").trim();
  }
  return normalized.replace(/\n+/g, " ").replace(/\s{2,}/g, " ").trim();
}
